import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text, Title} from '@dropbox/dig-components/typography';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {API_URL, isSandboxMode} from 'utilities';

import styles from './admin.module.css';

interface CycleTransitionCount {
  from_cycle: string;
  to_cycle: string;
  count: number;
}

interface ReturnMetricsResponse {
  full_to_diet: number;
  full_to_next_full: number;
  managers_returning: number;
  feedback_users_returning: number;
  full_to_diet_transitions: CycleTransitionCount[];
  full_to_next_full_transitions: CycleTransitionCount[];
  has_data: boolean;
}

interface ModuleRetentionRow {
  feature: string;
  daily: number;
  weekly: number;
  monthly: number;
}

interface ActiveReturnsResponse {
  modules: ModuleRetentionRow[];
  has_data: boolean;
}

const RETURN_METRICS_QUERY_KEY = '/admin/metrics/returns';
const ACTIVE_RETURNS_QUERY_KEY = '/admin/metrics/active-returns';

// Human-readable labels for the module identifiers returned by the backend.
const MODULE_LABELS: Record<string, string> = {
  feedback_assistant: 'Feedback Assistant',
  manager_assistant: 'Manager Assistant',
  career_frameworks: 'Career Frameworks',
};

const withSandbox = (url: string, isSandbox: boolean): string =>
  isSandbox ? `${url}?is_sandbox=true` : url;

const useReturnMetrics = () => {
  const isSandbox = isSandboxMode();
  return useQuery({
    queryKey: [RETURN_METRICS_QUERY_KEY, isSandbox],
    queryFn: async (): Promise<ReturnMetricsResponse> => {
      const response = await fetch(
        withSandbox(`${API_URL}/api/v1/admin/metrics/returns`, isSandbox),
        {credentials: 'include'}
      );
      if (!response.ok) {
        throw new Error('Failed to fetch return metrics');
      }
      return response.json();
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  });
};

const useModuleRetention = () => {
  const isSandbox = isSandboxMode();
  return useQuery({
    queryKey: [ACTIVE_RETURNS_QUERY_KEY, isSandbox],
    queryFn: async (): Promise<ActiveReturnsResponse> => {
      const response = await fetch(
        withSandbox(
          `${API_URL}/api/v1/admin/metrics/active-returns`,
          isSandbox
        ),
        {credentials: 'include'}
      );
      if (!response.ok) {
        throw new Error('Failed to fetch module retention metrics');
      }
      return response.json();
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  });
};

const useSeedSandboxMetrics = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await fetch(
        `${API_URL}/api/v1/admin/metrics/seed-sandbox?is_sandbox=true`,
        {method: 'PUT', credentials: 'include'}
      );
      if (!response.ok) {
        throw new Error('Failed to seed sandbox metrics');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RETURN_METRICS_QUERY_KEY, true],
      });
      queryClient.invalidateQueries({
        queryKey: [ACTIVE_RETURNS_QUERY_KEY, true],
      });
    },
  });
};

const buildMetricCards = (data: ReturnMetricsResponse) => [
  {
    label: 'Full \u2192 Diet returners',
    value: data.full_to_diet,
    description:
      'Used an assistant in a full SPRiTEs cycle and returned in the following Diet SPRiTEs cycle.',
  },
  {
    label: 'Full \u2192 next Full returners',
    value: data.full_to_next_full,
    description:
      'Used an assistant in a full SPRiTEs cycle and returned in the next full SPRiTEs cycle.',
  },
  {
    label: 'Managers returning',
    value: data.managers_returning,
    description:
      'Used the Manager Assistant / ReviewGPT in two consecutive cycles.',
  },
  {
    label: 'Feedback Assistant returners',
    value: data.feedback_users_returning,
    description: 'Used the Feedback Assistant in two consecutive cycles.',
  },
];

const TransitionsTable = ({
  title,
  transitions,
}: {
  title: string;
  transitions: CycleTransitionCount[];
}) => {
  if (transitions.length === 0) {
    return null;
  }
  return (
    <div className={styles['metrics-transitions']}>
      <Text variant="label" tagName="p" isBold>
        {title}
      </Text>
      <div className={styles['table-container']}>
        <table className={styles['opted-out-table']}>
          <thead className={styles['sticky-header']}>
            <tr>
              <th>From cycle</th>
              <th>To cycle</th>
              <th>Returning users</th>
            </tr>
          </thead>
          <tbody>
            {transitions.map((t) => (
              <tr key={`${t.from_cycle}->${t.to_cycle}`}>
                <td>{t.from_cycle}</td>
                <td>{t.to_cycle}</td>
                <td>{t.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ModuleRetentionTable = ({modules}: {modules: ModuleRetentionRow[]}) => {
  if (modules.length === 0) {
    return null;
  }
  return (
    <div className={styles['metrics-transitions']}>
      <Text variant="label" tagName="p" isBold>
        Returning users by module
      </Text>
      <Text variant="paragraph" tagName="p" color="subtle" size="small">
        Distinct users active in the current period and the period immediately
        before it. The current day, week, and month are still in progress, so
        these counts continue to grow until each period ends.
      </Text>
      <div className={styles['table-container']}>
        <table className={styles['opted-out-table']}>
          <thead className={styles['sticky-header']}>
            <tr>
              <th>Module</th>
              <th>Daily</th>
              <th>Weekly</th>
              <th>Monthly</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((module) => (
              <tr key={module.feature}>
                <td>{MODULE_LABELS[module.feature] ?? module.feature}</td>
                <td>{module.daily}</td>
                <td>{module.weekly}</td>
                <td>{module.monthly}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const MetricsTab = () => {
  const isSandbox = isSandboxMode();
  const {data, isLoading, isError} = useReturnMetrics();
  const {
    data: retentionData,
    isLoading: isRetentionLoading,
    isError: isRetentionError,
  } = useModuleRetention();
  const seedMutation = useSeedSandboxMetrics();

  return (
    <div className={styles['metrics-container']}>
      <div className={styles['metrics-section']}>
        <Title size="large" tagName="h2">
          Daily / Weekly / Monthly Return Metrics
        </Title>

        {isRetentionLoading && (
          <div className={styles['loading-container']}>
            <Spinner size="large" />
          </div>
        )}

        {isRetentionError && !isRetentionLoading && (
          <Text variant="paragraph" tagName="p">
            Failed to load module retention metrics.
          </Text>
        )}

        {retentionData && !isRetentionLoading && !isRetentionError && (
          <>
            {!retentionData.has_data && (
              <div className={styles['metrics-empty']}>
                <Text variant="paragraph" tagName="p">
                  No module activity has been recorded yet
                  {isSandbox
                    ? '. Use "Seed sandbox metrics" above to load mock data.'
                    : '. Counts will appear here as users use the modules.'}
                </Text>
              </div>
            )}
            <ModuleRetentionTable modules={retentionData.modules} />
          </>
        )}
      </div>

      <Title size="large" tagName="h1">
        Cross-Cycle Return Metrics
      </Title>
      <div className={styles['instructions']}>
        <Text variant="paragraph" tagName="p">
          These metrics count users who return to use SPRiTEsGPT across review
          cycles. Individual users are never identified here: each row is stored
          under an irreversible pseudonym, and only aggregate counts are shown.
        </Text>
        <Text variant="paragraph" tagName="p" color="subtle" size="small">
          Data is forward-looking. Because usage was not recorded historically,
          these numbers start empty and only become meaningful after a
          subsequent cycle begins.
        </Text>
      </div>

      {isSandbox && (
        <div className={styles['metrics-sandbox-actions']}>
          <Text variant="paragraph" tagName="p" color="subtle" size="small">
            Sandbox Mode is on. You can load mock data to preview this view.
          </Text>
          <Button
            variant="primary"
            size="small"
            onClick={() => seedMutation.mutate()}
            isLoading={seedMutation.isLoading}
            disabled={seedMutation.isLoading}
          >
            Seed sandbox metrics
          </Button>
          {seedMutation.isError && (
            <Text variant="paragraph" tagName="span" color="faint" size="small">
              Failed to seed sandbox metrics.
            </Text>
          )}
        </div>
      )}

      {isLoading && (
        <div className={styles['loading-container']}>
          <Spinner size="large" />
        </div>
      )}

      {isError && !isLoading && (
        <Text variant="paragraph" tagName="p">
          Failed to load return metrics.
        </Text>
      )}

      {data && !isLoading && !isError && (
        <>
          {!data.has_data && (
            <div className={styles['metrics-empty']}>
              <Text variant="paragraph" tagName="p">
                No usage has been recorded yet
                {isSandbox
                  ? '. Use "Seed sandbox metrics" above to load mock data.'
                  : '. Counts will appear here as users use the assistants across cycles.'}
              </Text>
            </div>
          )}

          <div className={styles['metrics-cards']}>
            {buildMetricCards(data).map((card) => (
              <div className={styles['metric-card']} key={card.label}>
                <div className={styles['metric-value']}>{card.value}</div>
                <div className={styles['metric-label']}>{card.label}</div>
                <Text
                  variant="paragraph"
                  tagName="p"
                  color="subtle"
                  size="small"
                >
                  {card.description}
                </Text>
              </div>
            ))}
          </div>

          <TransitionsTable
            title={'Full \u2192 Diet, by cycle'}
            transitions={data.full_to_diet_transitions}
          />
          <TransitionsTable
            title={'Full \u2192 next Full, by cycle'}
            transitions={data.full_to_next_full_transitions}
          />
        </>
      )}
    </div>
  );
};
